// Stockage local de l'application terrain (IndexedDB par idb, D-040) : travail téléchargé
// (missions, tournées, ordres de mission, référentiels), opérations en attente de
// synchronisation, photos en blobs, horodatages et session locale. Une seule base par origine ;
// les données d'un autre compte sont purgées avant d'enregistrer celles du compte connecté. Les objets
// emportés ont la forme du contrat (@cc/contrats, terrain.ts, proprete.ts, inspections.ts).
import type { MissionTerrain, ObjetPhotoTerrain, OperationTerrain, OrdreMissionTerrain, ResultatOperationTerrain, TourneeTerrain, TravailTerrain } from '@cc/contrats';
import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { TravailAppareil } from './appareil';

type Operation = OperationTerrain;
type ResultatSynchronisation = ResultatOperationTerrain;
type ObjetTypePhoto = ObjetPhotoTerrain;
type ReferentielsTerrain = TravailTerrain['referentiels'];

export const NOM_BASE = 'cc-terrain';
// Version 2 (raccordement du lot L3) : objets emportés à la forme du contrat ; l'ancien travail est vidé.
const VERSION_BASE = 2;

export type EtatOperationLocale = 'en_attente' | 'en_cours' | 'appliquee' | 'ignoree_doublon' | 'conflit' | 'rejetee';

/** Opération de la file : l'opération du contrat, son rang d'enregistrement et son suivi local. */
export interface OperationLocale extends Operation {
  rang: number;
  /** Libellé lisible (« Rapport après travaux ») pour les listes et l'écran des conflits. */
  titre: string;
  /** Libellé de l'objet visé au moment de la saisie (numéro et titre). */
  objet_libelle: string;
  etat: EtatOperationLocale;
  tentatives: number;
  derniere_erreur: string | null;
  resultat: ResultatSynchronisation | null;
  synchronisee_le: string | null;
}

export type EtatPhotoLocale = 'en_attente' | 'envoyee' | 'echec';

/** Photo réduite, en octets (ArrayBuffer : clonable partout, relue en Blob par blobPhoto). */
export interface PhotoLocale {
  id_client: string;
  operation_id_client: string;
  objet_type: ObjetTypePhoto;
  objet_id_client: string;
  donnees: ArrayBuffer;
  type: string;
  empreinte: string;
  largeur: number;
  hauteur: number;
  taille: number;
  prise_le: string;
  /** Ordre de saisie (rang de l'opération puis position de la photo) : ordre d'envoi stable. */
  ordre: number;
  etat: EtatPhotoLocale;
  document_id: string | null;
  tentatives: number;
  derniere_erreur: string | null;
}

export interface SessionLocale {
  compte_id: string;
  identifiant: string;
  nom: string;
  appartenance_id: string;
  collectivite: string | null;
  ouverte_le: string;
  renouvelee_le: string;
  expire_le: string;
  /** Code local à six chiffres (D-029) : sel et empreinte SHA-256, jamais le code. */
  code: { sel: string; empreinte: string } | null;
}

export interface Horodatages {
  travail_telecharge_le: string | null;
  horodatage_serveur: string | null;
  derniere_synchronisation: string | null;
  dernier_contact_serveur: string | null;
}

export interface TravailLocal extends TravailAppareil {
  telecharge_le: string;
}

interface BaseTerrain extends DBSchema {
  meta: { key: string; value: unknown };
  missions: { key: string; value: MissionTerrain };
  tournees: { key: string; value: TourneeTerrain };
  ordres: { key: string; value: OrdreMissionTerrain };
  operations: { key: string; value: OperationLocale; indexes: { 'par-rang': number; 'par-objet': string } };
  photos: { key: string; value: PhotoLocale; indexes: { 'par-operation': string } };
}

const HORODATAGES_VIDES: Horodatages = { travail_telecharge_le: null, horodatage_serveur: null, derniere_synchronisation: null, dernier_contact_serveur: null };

let connexion: Promise<IDBPDatabase<BaseTerrain>> | null = null;

export function ouvrirBase(): Promise<IDBPDatabase<BaseTerrain>> {
  if (!connexion) {
    connexion = openDB<BaseTerrain>(NOM_BASE, VERSION_BASE, {
      upgrade(b, ancienne, _nouvelle, tx) {
        if (ancienne < 1) {
          b.createObjectStore('meta');
          b.createObjectStore('missions', { keyPath: 'id' });
          b.createObjectStore('tournees', { keyPath: 'id' });
          b.createObjectStore('ordres', { keyPath: 'id' });
          const operations = b.createObjectStore('operations', { keyPath: 'id_client' });
          operations.createIndex('par-rang', 'rang');
          operations.createIndex('par-objet', 'objet_id');
          const photos = b.createObjectStore('photos', { keyPath: 'id_client' });
          photos.createIndex('par-operation', 'operation_id_client');
        } else if (ancienne < 2) {
          // Travail de l'ancienne forme vidé (il sera retéléchargé) ; la file et les photos sont gardées.
          void tx.objectStore('missions').clear();
          void tx.objectStore('tournees').clear();
          void tx.objectStore('ordres').clear();
          void tx.objectStore('meta').delete('equipe');
        }
      },
      terminated() {
        connexion = null;
      },
    });
  }
  return connexion;
}

export async function fermerBase(): Promise<void> {
  if (!connexion) return;
  const b = await connexion;
  b.close();
  connexion = null;
}

/** Supprime toute la base (réglage « vider le cache », changement de compte). */
export async function supprimerBase(): Promise<void> {
  await fermerBase();
  await deleteDB(NOM_BASE);
}

export async function lireMeta<T>(cle: string): Promise<T | null> {
  const b = await ouvrirBase();
  return ((await b.get('meta', cle)) as T | undefined) ?? null;
}

export async function ecrireMeta(cle: string, valeur: unknown): Promise<void> {
  const b = await ouvrirBase();
  await b.put('meta', valeur, cle);
}

export async function lireHorodatages(): Promise<Horodatages> {
  return { ...HORODATAGES_VIDES, ...((await lireMeta<Partial<Horodatages>>('horodatages')) ?? {}) };
}

export async function ecrireHorodatages(partiel: Partial<Horodatages>): Promise<Horodatages> {
  const h = { ...(await lireHorodatages()), ...partiel };
  await ecrireMeta('horodatages', h);
  return h;
}

/** Remplace le travail emporté par la réponse du serveur ; les opérations et photos sont conservées. */
export async function enregistrerTravail(t: TravailAppareil, maintenant = new Date()): Promise<void> {
  const b = await ouvrirBase();
  const tx = b.transaction(['missions', 'tournees', 'ordres', 'meta'], 'readwrite');
  await Promise.all([tx.objectStore('missions').clear(), tx.objectStore('tournees').clear(), tx.objectStore('ordres').clear()]);
  for (const m of t.missions) await tx.objectStore('missions').put(m);
  for (const r of t.tournees) await tx.objectStore('tournees').put(r);
  for (const o of t.ordres_mission) await tx.objectStore('ordres').put(o);
  const meta = tx.objectStore('meta');
  await meta.put(t.referentiels, 'referentiels');
  await meta.put(t.agent, 'agent');
  const anciens = ((await meta.get('horodatages')) as Partial<Horodatages> | undefined) ?? {};
  const horodatages: Horodatages = { ...HORODATAGES_VIDES, ...anciens, travail_telecharge_le: maintenant.toISOString(), horodatage_serveur: t.horodatage, dernier_contact_serveur: maintenant.toISOString() };
  await meta.put(horodatages, 'horodatages');
  await tx.done;
}

/** Travail tel qu'il est sur l'appareil ; null tant qu'aucun téléchargement n'a eu lieu. */
export async function lireTravail(): Promise<TravailLocal | null> {
  const b = await ouvrirBase();
  const horodatages = { ...HORODATAGES_VIDES, ...(((await b.get('meta', 'horodatages')) as Partial<Horodatages> | undefined) ?? {}) };
  if (!horodatages.travail_telecharge_le) return null;
  const [missions, tournees, ordres_mission, referentiels, agent] = await Promise.all([
    b.getAll('missions'),
    b.getAll('tournees'),
    b.getAll('ordres'),
    b.get('meta', 'referentiels') as Promise<ReferentielsTerrain | undefined>,
    b.get('meta', 'agent') as Promise<TravailAppareil['agent'] | undefined>,
  ]);
  return {
    agent: agent ?? null,
    missions,
    tournees,
    ordres_mission,
    referentiels: referentiels ?? { categories: [], priorites: [], formulaires: [] },
    horodatage: horodatages.horodatage_serveur ?? horodatages.travail_telecharge_le,
    telecharge_le: horodatages.travail_telecharge_le,
  };
}

export async function lireMission(id: string): Promise<MissionTerrain | null> {
  return (await (await ouvrirBase()).get('missions', id)) ?? null;
}

export async function lireTournee(id: string): Promise<TourneeTerrain | null> {
  return (await (await ouvrirBase()).get('tournees', id)) ?? null;
}

export async function lireOrdre(id: string): Promise<OrdreMissionTerrain | null> {
  return (await (await ouvrirBase()).get('ordres', id)) ?? null;
}

/** Applique une version renvoyée par le serveur à l'objet local (résultat « appliquée »). */
export async function appliquerVersion(objetType: Operation['objet_type'], id: string, version: number): Promise<void> {
  const b = await ouvrirBase();
  if (objetType === 'emplacement') {
    const tx = b.transaction('meta', 'readwrite');
    const places = await tx.store.get('places-marches') as Record<string, { position_version?: number }> | undefined;
    if (places?.[id]) await tx.store.put({ ...places, [id]: { ...places[id], position_version: version } }, 'places-marches');
    await tx.done;
    return;
  }
  const magasin = objetType === 'mission' ? 'missions' : objetType === 'tournee' ? 'tournees' : 'ordres';
  const tx = b.transaction(magasin, 'readwrite');
  const objet = await tx.store.get(id);
  if (objet) await tx.store.put({ ...objet, version } as MissionTerrain & TourneeTerrain & OrdreMissionTerrain);
  await tx.done;
}

export interface PhotoAEnregistrer {
  id_client: string;
  objet_type: ObjetTypePhoto;
  fichier: Blob;
  empreinte: string;
  largeur: number;
  hauteur: number;
  prise_le: string;
}

/** Enregistre une opération et ses photos dans une même transaction ; le rang suit l'ordre de saisie. */
export async function enfilerOperation(op: Operation, extras: { titre: string; objet_libelle: string; photos?: PhotoAEnregistrer[] }): Promise<OperationLocale> {
  // Les octets sont lus avant la transaction : une attente asynchrone étrangère la fermerait.
  const photos = await Promise.all((extras.photos ?? []).map(async (p) => ({ ...p, donnees: await p.fichier.arrayBuffer(), type: p.fichier.type || 'image/jpeg' })));
  const b = await ouvrirBase();
  const tx = b.transaction(['operations', 'photos', 'meta'], 'readwrite');
  const dernier = ((await tx.objectStore('meta').get('rang')) as number | undefined) ?? 0;
  const rang = dernier + 1;
  await tx.objectStore('meta').put(rang, 'rang');
  const locale: OperationLocale = { ...op, rang, titre: extras.titre, objet_libelle: extras.objet_libelle, etat: 'en_attente', tentatives: 0, derniere_erreur: null, resultat: null, synchronisee_le: null };
  await tx.objectStore('operations').put(locale);
  for (const [position, p] of photos.entries()) {
    await tx.objectStore('photos').put({
      ordre: rang * 1000 + position,
      id_client: p.id_client,
      operation_id_client: op.id_client,
      objet_type: p.objet_type,
      objet_id_client: p.objet_type === 'emplacement' ? op.objet_id : op.id_client,
      donnees: p.donnees,
      type: p.type,
      empreinte: p.empreinte,
      largeur: p.largeur,
      hauteur: p.hauteur,
      taille: p.donnees.byteLength,
      prise_le: p.prise_le,
      etat: 'en_attente',
      document_id: null,
      tentatives: 0,
      derniere_erreur: null,
    });
  }
  await tx.done;
  return locale;
}

/** Blob d'une photo locale, pour l'envoi ou l'aperçu. */
export function blobPhoto(p: Pick<PhotoLocale, 'donnees' | 'type'>): Blob {
  return new Blob([p.donnees], { type: p.type || 'image/jpeg' });
}

export async function listerOperations(filtre: { etats?: EtatOperationLocale[]; objet_id?: string } = {}): Promise<OperationLocale[]> {
  const b = await ouvrirBase();
  const toutes = filtre.objet_id ? await b.getAllFromIndex('operations', 'par-objet', filtre.objet_id) : await b.getAllFromIndex('operations', 'par-rang');
  const etats = filtre.etats ? new Set(filtre.etats) : null;
  return toutes.filter((o) => !etats || etats.has(o.etat)).sort((a, c) => a.rang - c.rang);
}

export async function lireOperation(idClient: string): Promise<OperationLocale | null> {
  return (await (await ouvrirBase()).get('operations', idClient)) ?? null;
}

export async function mettreAJourOperation(idClient: string, changements: Partial<OperationLocale>): Promise<OperationLocale | null> {
  const b = await ouvrirBase();
  const tx = b.transaction('operations', 'readwrite');
  const actuelle = await tx.store.get(idClient);
  if (!actuelle) return null;
  const suivante = { ...actuelle, ...changements, id_client: idClient };
  await tx.store.put(suivante);
  await tx.done;
  return suivante;
}

export async function listerPhotos(operationIdClient?: string): Promise<PhotoLocale[]> {
  const b = await ouvrirBase();
  const photos = operationIdClient ? await b.getAllFromIndex('photos', 'par-operation', operationIdClient) : await b.getAll('photos');
  // L'heure de prise ne suffit pas : deux photos peuvent porter la même milliseconde.
  return photos.sort((a, c) => (a.ordre ?? 0) - (c.ordre ?? 0) || a.prise_le.localeCompare(c.prise_le));
}

export async function lirePhoto(idClient: string): Promise<PhotoLocale | null> {
  return (await (await ouvrirBase()).get('photos', idClient)) ?? null;
}

export async function mettreAJourPhoto(idClient: string, changements: Partial<PhotoLocale>): Promise<void> {
  const b = await ouvrirBase();
  const tx = b.transaction('photos', 'readwrite');
  const actuelle = await tx.store.get(idClient);
  if (actuelle) await tx.store.put({ ...actuelle, ...changements, id_client: idClient });
  await tx.done;
}

export interface CompteurAttente {
  operations: number;
  photos: number;
  conflits: number;
  rejets: number;
}

export async function compterAttente(): Promise<CompteurAttente> {
  const [operations, photos] = await Promise.all([listerOperations(), listerPhotos()]);
  return {
    operations: operations.filter((o) => o.etat === 'en_attente' || o.etat === 'en_cours').length,
    photos: photos.filter((p) => p.etat !== 'envoyee').length,
    conflits: operations.filter((o) => o.etat === 'conflit').length,
    rejets: operations.filter((o) => o.etat === 'rejetee').length,
  };
}

export async function lireSessionLocale(): Promise<SessionLocale | null> {
  return lireMeta<SessionLocale>('session');
}

export async function ecrireSessionLocale(s: SessionLocale): Promise<void> {
  await ecrireMeta('session', s);
}

export async function effacerSessionLocale(): Promise<void> {
  const b = await ouvrirBase();
  await b.delete('meta', 'session');
}

/** Efface le travail, la file et les photos ; conserve la session locale et son code. */
export async function viderDonneesTravail(): Promise<void> {
  const b = await ouvrirBase();
  const tx = b.transaction(['missions', 'tournees', 'ordres', 'operations', 'photos', 'meta'], 'readwrite');
  await Promise.all([tx.objectStore('missions').clear(), tx.objectStore('tournees').clear(), tx.objectStore('ordres').clear(), tx.objectStore('operations').clear(), tx.objectStore('photos').clear()]);
  const meta = tx.objectStore('meta');
  await Promise.all([meta.delete('horodatages'), meta.delete('referentiels'), meta.delete('agent'), meta.delete('rang'), meta.delete('places-marches')]);
  await tx.done;
}

/** Taille approximative des données locales (photos surtout), pour l'écran des réglages. */
export async function tailleDonnees(): Promise<{ photos: number; operations: number; octets: number }> {
  const [operations, photos] = await Promise.all([listerOperations(), listerPhotos()]);
  return { photos: photos.length, operations: operations.length, octets: photos.reduce((s, p) => s + p.taille, 0) };
}
